import { MfaMethod, type MfaVerification } from '@/api/mfa/types';

export const MFA_VERIFICATION_REQUIRED = 'USER.MFA.VERIFICATION_REQUIRED';

type ErrorPayload = {
  code?: unknown;
  data?: { context?: { verification?: unknown } };
};

const errorPayload = (error: unknown): ErrorPayload | null => {
  if (typeof error !== 'object' || error === null || !('data' in error)) {
    return null;
  }
  const data = (error as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) {
    return null;
  }
  return data as ErrorPayload;
};

export const getMfaErrorCode = (error: unknown): string | null => {
  const code = errorPayload(error)?.code;
  if (typeof code === 'string') {
    return code;
  }
  return null;
};

const isVerification = (value: unknown): value is MfaVerification => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Partial<MfaVerification>;
  return (
    typeof candidate.interactionToken === 'string' &&
    typeof candidate.expiresAt === 'string' &&
    Number.isFinite(Date.parse(candidate.expiresAt)) &&
    Array.isArray(candidate.allowedMethods) &&
    candidate.allowedMethods.length > 0 &&
    candidate.allowedMethods.every(
      (method) => method === MfaMethod.Totp || method === MfaMethod.Passkey,
    ) &&
    (candidate.publicKey === undefined ||
      (typeof candidate.publicKey === 'object' && candidate.publicKey !== null))
  );
};

export const parseMfaVerificationRequired = (error: unknown): MfaVerification | null => {
  const payload = errorPayload(error);
  if (payload?.code !== MFA_VERIFICATION_REQUIRED) {
    return null;
  }
  const verification = payload.data?.context?.verification;
  if (!isVerification(verification)) {
    return null;
  }
  return verification;
};

const MESSAGE_BY_CODE: Record<string, string> = {
  'USER.MFA.VERIFICATION_FAILED': 'Incorrect code. Please try again.',
  'USER.MFA.TOTP_STEP_REPLAYED': 'That code was already used. Wait for a new code and try again.',
  'USER.MFA.WORKFLOW_INVALID': 'This request expired. Restart the MFA action to try again.',
  'USER.MFA.ORIGIN_NOT_ALLOWED':
    'This request could not be confirmed. Restart the MFA action to try again.',
  'USER.MFA.UNAVAILABLE': 'MFA is temporarily unavailable. Please try again later.',
  'USER.MFA.CREDENTIAL_SET_STALE': 'Your MFA methods changed. Restart this action to continue.',
  'USER.MFA.TOTP_ALREADY_ACTIVE': 'An authenticator is already active.',
  'USER.MFA.PASSKEY_ALREADY_REGISTERED': 'This passkey is already registered.',
};

export const getMfaErrorMessage = (
  error: unknown,
  fallback = 'MFA could not be completed. Try again.',
): string => {
  const code = getMfaErrorCode(error);
  if (typeof code !== 'string') {
    return fallback;
  }
  return MESSAGE_BY_CODE[code] ?? fallback;
};
